import type {DigitalSession, LibraryState, StudentAttempt} from "./activity-library";

export type LiveUpdate = {
  attempts: StudentAttempt[];
  session: DigitalSession | null;
  removedAttemptIds: string[];
  presence: {attemptId: string; studentIds: string[]}[];
};

export function mergeLiveUpdate(state: LibraryState, update: LiveUpdate): LibraryState {
  let changed = false;
  const attempts = state.attempts.filter(a => {
    if (!update.removedAttemptIds.includes(a.id)) return true;
    changed = true; return false;
  }).map(a => {
    const incoming = update.attempts.find(b => b.id === a.id);
    if (!incoming || incoming.revision <= a.revision) return a;
    changed = true; return incoming;
  });
  for (const a of update.attempts) if (!attempts.some(b => b.id === a.id)) {attempts.push(a); changed = true;}
  const session = update.session;
  const digitalSessions = (state.digitalSessions ?? []).map(s => {
    if (!session || s.id !== session.id || session.revision < s.revision || JSON.stringify(session) === JSON.stringify(s)) return s;
    changed = true; return session;
  });
  if (session && !digitalSessions.some(s => s.id === session.id)) {digitalSessions.push(session); changed = true;}
  return changed ? {...state, attempts, digitalSessions} : state;
}

export function changedAnswers(base: Record<string, string>, current: Record<string, string>, serverOwnedIds:readonly string[] = []) {
  return Object.fromEntries(Object.entries(current).filter(([id, value]) => !serverOwnedIds.includes(id) && value !== (base[id] ?? "")));
}

/** Keep typing while a save is in flight; merge other members' disjoint changes. */
export function reconcileAnswers(base: Record<string, string>, local: Record<string, string>, incoming: Record<string, string>, serverOwnedIds:readonly string[] = []) {
  const answers = {...incoming};
  const conflicts: string[] = [];
  // Laboratory worlds are confirmed command results, never unsaved text drafts.
  for (const [id, value] of Object.entries(changedAnswers(base, local, serverOwnedIds))) {
    if ((incoming[id] ?? "") !== (base[id] ?? "") && incoming[id] !== value) conflicts.push(id);
    answers[id] = value;
  }
  return {answers, conflicts};
}

/** A command can finish before its host rerenders the corresponding props. */
export function latestAcknowledgedAttempt(incoming:StudentAttempt|undefined, acknowledged:StudentAttempt|undefined){
  return incoming && acknowledged?.id===incoming.id && acknowledged.revision>incoming.revision ? acknowledged : incoming;
}
